import { configs } from '../configs';

export interface ChecklistItem {
  id: string;
  title: string;
  description?: string;
  category:
    | 'opening'
    | 'closing'
    | 'maintenance'
    | 'customer_service'
    | 'custom';
  priority: 'low' | 'medium' | 'high';
  isCompleted: boolean;
  completedAt?: string;
  completedBy?: string;
  notes?: string;
  photoEvidence?: string;
  estimatedDuration: number; // in minutes
  actualDuration?: number;
  date?: string;
  userId?: string;
  tenantId?: string;
  createdAt?: string;
  updatedAt?: string;
}

class ChecklistService {
  private async getAuthHeader(): Promise<Record<string, string>> {
    try {
      const tokenRaw =
        localStorage.getItem('auth.accessToken') ||
        localStorage.getItem('authToken') ||
        localStorage.getItem('token');

      if (tokenRaw) {
        let token = tokenRaw;
        try {
          const parsed = JSON.parse(tokenRaw);
          if (parsed && typeof parsed === 'object' && parsed.value) {
            token = parsed.value;
          }
        } catch (e) {
          token = tokenRaw;
        }

        return {
          Authorization: `Bearer ${token}`,
        };
      }

      const sessionTokenRaw =
        sessionStorage.getItem('auth.accessToken') ||
        sessionStorage.getItem('authToken') ||
        sessionStorage.getItem('token');
      if (sessionTokenRaw) {
        let sessionToken = sessionTokenRaw;
        try {
          const parsed = JSON.parse(sessionTokenRaw);
          if (parsed && typeof parsed === 'object' && parsed.value) {
            sessionToken = parsed.value;
          }
        } catch (e) {
          sessionToken = sessionTokenRaw;
        }

        return {
          Authorization: `Bearer ${sessionToken}`,
        };
      }

      return {};
    } catch (error) {
      console.error('Error getting auth header:', error);
      return {};
    }
  }

  async getChecklistItems(date?: string): Promise<ChecklistItem[]> {
    try {
      const authHeaders = await this.getAuthHeader();
      const dateParam = date ? `?date=${date}` : '';
      const response = await fetch(
        `${configs.apiRoot}/daily_checklists${dateParam}`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
        }
      );

      if (!response.ok) {
        // If API doesn't exist yet, return empty array
        if (response.status === 404) {
          console.warn('Daily checklist API not yet implemented');
          return [];
        }
        throw new Error(`Failed to fetch checklist items: ${response.status}`);
      }

      const data = await response.json();
      // Handle both array and object with items property
      return Array.isArray(data) ? data : data.items || [];
    } catch (error) {
      console.error('Error fetching checklist items:', error);
      // Return empty array instead of throwing to allow graceful degradation
      return [];
    }
  }

  async getCurrentChecklist(): Promise<ChecklistItem[]> {
    try {
      const authHeaders = await this.getAuthHeader();
      const response = await fetch(`${configs.apiRoot}/daily_checklists/current`, {
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          return [];
        }
        throw new Error(`Failed to fetch current checklist: ${response.status}`);
      }

      const data = await response.json();
      return Array.isArray(data) ? data : data.items || [];
    } catch (error) {
      console.error('Error fetching current checklist:', error);
      return [];
    }
  }

  async createChecklistItem(item: Partial<ChecklistItem>): Promise<ChecklistItem> {
    try {
      const authHeaders = await this.getAuthHeader();
      const response = await fetch(`${configs.apiRoot}/daily_checklists`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify(item),
      });

      if (!response.ok) {
        throw new Error(`Failed to create checklist item: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating checklist item:', error);
      throw error;
    }
  }

  async updateChecklistItem(
    id: string,
    item: Partial<ChecklistItem>
  ): Promise<ChecklistItem> {
    try {
      const authHeaders = await this.getAuthHeader();
      const response = await fetch(`${configs.apiRoot}/daily_checklists/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
        body: JSON.stringify(item),
      });

      if (!response.ok) {
        throw new Error(`Failed to update checklist item: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error updating checklist item ${id}:`, error);
      throw error;
    }
  }

  async deleteChecklistItem(id: string): Promise<void> {
    try {
      const authHeaders = await this.getAuthHeader();
      const response = await fetch(`${configs.apiRoot}/daily_checklists/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete checklist item: ${response.status}`);
      }
    } catch (error) {
      console.error(`Error deleting checklist item ${id}:`, error);
      throw error;
    }
  }

  async toggleItemCompletion(
    id: string,
    isCompleted: boolean,
    completedBy?: string,
    notes?: string
  ): Promise<ChecklistItem> {
    try {
      const authHeaders = await this.getAuthHeader();
      const response = await fetch(
        `${configs.apiRoot}/daily_checklists/${id}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...authHeaders,
          },
          body: JSON.stringify({
            isCompleted,
            completedBy,
            completedAt: isCompleted ? new Date().toISOString() : undefined,
            notes,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to toggle checklist item: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error toggling checklist item ${id}:`, error);
      throw error;
    }
  }
}

export const checklistService = new ChecklistService();

